import { motion } from "framer-motion";
import {
  AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip,
  RadialBarChart, RadialBar, PolarAngleAxis, BarChart, Bar, CartesianGrid,
} from "recharts";
import { TrendingUp, MousePointerClick, Target, IndianRupee } from "lucide-react";

const perf = [
  { d: "Mon", leads: 42, spend: 3200 },
  { d: "Tue", leads: 58, spend: 3400 },
  { d: "Wed", leads: 71, spend: 3600 },
  { d: "Thu", leads: 63, spend: 3550 },
  { d: "Fri", leads: 92, spend: 3800 },
  { d: "Sat", leads: 118, spend: 4000 },
  { d: "Sun", leads: 134, spend: 4200 },
];

const campaigns = [
  { name: "Meta", v: 78 },
  { name: "IG", v: 64 },
  { name: "FB", v: 71 },
  { name: "WA", v: 55 },
];

const roi = [{ name: "ROAS", value: 4.7, fill: "#D4AF37" }];

const tooltipStyle = {
  background: "#0A1128",
  border: "1px solid rgba(212,175,55,0.35)",
  borderRadius: 10,
  color: "#fff",
  fontSize: 12,
};

export const AIDashboard = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="relative"
    >
      {/* Gold glow behind */}
      <div className="absolute -inset-6 bg-[radial-gradient(closest-side,rgba(212,175,55,0.18),transparent)] blur-2xl" />

      <div className="relative animate-float">
        <div className="glass rounded-3xl p-5 sm:p-6 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]">
          {/* Header */}
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg grid place-items-center bg-[#D4AF37]/15 border border-[#D4AF37]/30">
                <Target size={16} className="text-[#D4AF37]" />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37]">Live Dashboard</div>
                <div className="text-sm text-white font-semibold">Meta Ads Performance</div>
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-[11px] text-white/70">Realtime</span>
            </div>
          </div>

          {/* KPI row */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            <KPI icon={<MousePointerClick size={14} />} label="Leads" value="1,284" delta="+38%" />
            <KPI icon={<IndianRupee size={14} />} label="CPL" value="₹42" delta="-19%" positive />
            <KPI icon={<TrendingUp size={14} />} label="Conv." value="6.8%" delta="+2.1%" positive />
          </div>

          {/* Area chart */}
          <div className="rounded-xl bg-white/[0.02] border border-white/5 p-3 mb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs text-white/70">Weekly Lead Growth</div>
              <div className="text-[11px] text-[#D4AF37]">+184% MoM</div>
            </div>
            <div style={{ height: 130 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={perf} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="areaGold" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="d" tick={{ fill: "#94A3B8", fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Area type="monotone" dataKey="leads" stroke="#D4AF37" strokeWidth={2} fill="url(#areaGold)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Bottom row: ROI gauge + campaigns */}
          <div className="grid grid-cols-5 gap-3">
            <div className="col-span-2 rounded-xl bg-white/[0.02] border border-white/5 p-3">
              <div className="text-xs text-white/70 mb-1">ROAS</div>
              <div style={{ height: 110 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RadialBarChart innerRadius="72%" outerRadius="100%" data={roi} startAngle={210} endAngle={-30}>
                    <PolarAngleAxis type="number" domain={[0, 6]} tick={false} />
                    <RadialBar background={{ fill: "rgba(255,255,255,0.06)" }} dataKey="value" cornerRadius={20} />
                  </RadialBarChart>
                </ResponsiveContainer>
              </div>
              <div className="-mt-14 text-center relative">
                <div className="text-2xl font-bold text-white">4.7x</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">Return</div>
              </div>
            </div>

            <div className="col-span-3 rounded-xl bg-white/[0.02] border border-white/5 p-3">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs text-white/70">Campaign Performance</div>
                <div className="text-[11px] text-[#D4AF37]">Top: Meta</div>
              </div>
              <div style={{ height: 108 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={campaigns} margin={{ top: 8, right: 5, left: -25, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="name" tick={{ fill: "#94A3B8", fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#64748B", fontSize: 10 }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "rgba(212,175,55,0.08)" }} />
                    <Bar dataKey="v" fill="#D4AF37" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Floating mini card */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="hidden md:flex absolute -left-6 -bottom-6 items-center gap-3 px-4 py-3 glass rounded-2xl"
        >
          <div className="w-9 h-9 rounded-full bg-emerald-400/15 border border-emerald-400/40 grid place-items-center text-emerald-300">
            <TrendingUp size={16} />
          </div>
          <div>
            <div className="text-white text-sm font-semibold">Sales Growth</div>
            <div className="text-[11px] text-white/60">+62% this quarter</div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

const KPI = ({ icon, label, value, delta, positive }) => (
  <div className="rounded-xl bg-white/[0.02] border border-white/5 p-3">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-white/60">
      <span className="text-[#D4AF37]">{icon}</span> {label}
    </div>
    <div className="mt-1.5 flex items-baseline justify-between">
      <div className="text-lg font-bold text-white">{value}</div>
      <div className={`text-[11px] font-medium ${positive ? "text-emerald-400" : "text-[#D4AF37]"}`}>{delta}</div>
    </div>
  </div>
);
